import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useLazyQuery } from "@apollo/client";
import { CircularProgress } from "@mui/material";
import {
  GET_DEFAULT_LOCATION,
  GET_SERVICES_MERCH_SKU_DETAILS,
} from "../../graphql/queries";
import {
  BALLOONS,
  DATA_FETCH_ERROR,
  ONLOAN_INVALID_STOREID_ERROR,
  NO_CONNECTION_ERROR,
} from "../../common/constants";
import "./styles.scss";
import { connect } from "react-redux";
import { getDate, getStoreChecker } from "../../utils/utils";
import ErrorMessage from "../ErrorMessage";
import ServicesMerchDataRow from "./ServicesMerchDataRow";
import useNetwork from "../UseNetwork";
import Typography from "@mui/material/Typography";

const SERVICES_MERCH_TYPE = BALLOONS.toUpperCase();

const ServicesMerchDetailTab = ({ employeeInfo = {}, dateMatrix }) => {
  const networkStatus = useNetwork();
  const [selectedLocation, setSelectedLocation] = React.useState(
    JSON.parse(sessionStorage.getItem("selectedLocation"))
  );
  const userEmail = employeeInfo?.adUpn;
  let selectedStoreId = localStorage.getItem("storeId");
  const activeStorechecker = getStoreChecker(selectedStoreId);

  const [getDefaultLocation, { error: locationError = {} }] = useLazyQuery(
    GET_DEFAULT_LOCATION,
    {
      onCompleted: (data) => {
        let { salesSummaryV2 } = data;
        if (selectedLocation.locationName === "NA") {
          setSelectedLocation({
            locationNo: salesSummaryV2?.location?.primaryLocationNumber,
            locationName: salesSummaryV2?.location?.primaryLocationName,
          });
          sessionStorage.setItem(
            "selectedLocation",
            JSON.stringify({
              locationNo: salesSummaryV2?.location?.primaryLocationNumber,
              locationName: salesSummaryV2?.location?.primaryLocationName,
            })
          );
        }
      },
    }
  );

  useEffect(() => {
    if (userEmail && (activeStorechecker === true || selectedStoreId === "")) {
      getDefaultLocation({
        variables: { request: { email: userEmail, storeId: selectedStoreId } },
        context: {
          headers: {
            Authorization: localStorage.getItem("jwtToken"),
          },
        },
      });
    }
  }, [userEmail, getDefaultLocation, selectedStoreId, activeStorechecker]);

  const [
    getServicesMerchSkuSummary,
    {
      data: { servicesSkuSummary: { servicesSkuSalesReport = [] } = {} } = {},
      error: serviceError = {},
      loading: serviceLoading,
    },
  ] = useLazyQuery(GET_SERVICES_MERCH_SKU_DETAILS);

  useEffect(() => {
    if (
      userEmail &&
      selectedLocation &&
      selectedLocation?.locationName !== "NA" &&
      dateMatrix
    ) {
      getServicesMerchSkuSummary({
        variables: {
          request: {
            email: userEmail,
            location: {
              primaryLocationNumber: selectedLocation?.locationNo,
              primaryLocationName: selectedLocation?.locationName,
            },
            toDate: getDate("YESTERDAY", ""),
            matrixType: dateMatrix === "YESTERDAY" ? "DAY" : dateMatrix,
            servicesTypeName: SERVICES_MERCH_TYPE,
          },
        },
        context: {
          headers: {
            Authorization: localStorage.getItem("jwtToken"),
          },
        },
      });
    }
  }, [
    userEmail,
    selectedLocation,
    dateMatrix,
    getServicesMerchSkuSummary,
  ]);

  return (
    <div className="services-merch-detail-tab" data-testid="services-merch-detail-tab">
      {!networkStatus && <ErrorMessage message={NO_CONNECTION_ERROR} />}
      {serviceLoading && (
        <div className="services-merch-detail-loader">
          <CircularProgress color="primary" size={30} />
        </div>
      )}
      {!serviceLoading && (
        <>
          {serviceError.message && (
            <ErrorMessage
              statusCode={
                serviceError?.networkError?.statusCode ||
                locationError?.networkError?.statusCode
              }
              message={DATA_FETCH_ERROR}
            />
          )}

          {activeStorechecker === false && selectedStoreId ? (
            <ErrorMessage message={ONLOAN_INVALID_STOREID_ERROR} />
          ) : null}
          {!servicesSkuSalesReport?.length > 0 ? (
            <div className="services-merch-noData-card">
              <div className="no-data-row">
                <Typography component="h1" variant="h1" className="label">
                  There is no data available for this timeframe. Please go back
                  and try a new selection{" "}
                </Typography>
              </div>
            </div>
          ) : (
            <div className="table-container">
              <div className="services-merch-label-container flex row">
                <div className="services-merch-sales-label">Sales U</div>
                <div className="services-merch-sales-label">Sales $</div>
              </div>
              <div className="border-row top-border-row"></div>
              <div
                className={
                  !servicesSkuSalesReport?.length > 0
                    ? "table-content-nodata"
                    : "table-content"
                }
              >
                {servicesSkuSalesReport?.length > 0 &&
                  servicesSkuSalesReport.map(
                    (
                      { itemNumber, itemName, totalSalesUnits, salesAmount },
                      index
                    ) => {
                      return (
                        <ServicesMerchDataRow
                          key={`${itemNumber}-${index}`}
                          serviceitemNumber={itemNumber}
                          serviceitemName={itemName}
                          servicesalesu={totalSalesUnits}
                          servicesalesdollar={salesAmount}
                        />
                      );
                    }
                  )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

ServicesMerchDetailTab.propTypes = {
  employeeInfo: PropTypes.shape({
    adUpn: PropTypes.string,
  }),
  dateMatrix: PropTypes.string,
};

const mapStateToProps = (state) => ({
  employeeInfo: state.auth.employeeInfo,
  dateMatrix: state?.salesSummary?.currentDateMatrix?.value,
});

export default connect(mapStateToProps, undefined)(ServicesMerchDetailTab);
